// Layer: Page (UI) – chỉ render JSX, toàn bộ logic quản lý chấm công nằm trong hook domain `useTimekeepingManagement`.
import React from "react";
import {
  Typography,
  Input,
  Select,
  DatePicker,
  Table,
  Tag,
  Space,
  Button,
  Tooltip,
  Popconfirm,
  Avatar,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import "dayjs/locale/vi";
import { Pencil, Trash2, User } from "lucide-react";
import { FilterSection } from "../components/common/FilterSection";
import { Pagination } from "../components/common/Pagination";
import {
  TimekeepingModal,
  type TimekeepingFormData,
} from "../components/timekeeping/TimekeepingModal";
import { STATUS_OPTIONS } from "@/features/timekeeping/constants";
import type {
  TimekeepingStatus,
  TimekeepingRecord,
} from "../types/timekeeping";
import {
  useTimekeepingManagement,
  type DateRange,
} from "@/features/timekeeping/useTimekeepingManagement";
import listChecksIcon from "../assets/list-checks.svg";
import timerOffIcon from "../assets/timer-off.svg";
import clockPlusIcon from "../assets/clock-plus.svg";
import clockCheckIcon from "../assets/clock-check.svg";
import searchIcon from "../assets/search-icon.svg";
import userRoundPenIcon from "../assets/user-round-pen.svg";
import positioningIcon from "../assets/positioning.svg";
import styles from "./TimekeepingManagementPage.module.css";
import { loadAvatarFromData } from "@/utils/avatarLoader";

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { Option } = Select;

const statusTagClass: Record<TimekeepingStatus, string> = {
  "Đủ công": styles.statusSuccess,
  "Tăng ca": styles.statusInfo,
  "Đi trễ": styles.statusWarning,
  "Về sớm": styles.statusOrange,
  "Đã check-in": styles.statusPurple,
  "Vắng không lương": styles.statusPink,
  "Vắng có lương": styles.statusCyan,
  "Vắng mặt": styles.statusError,
};

const iconMap: Record<string, string> = {
  "list-checks.svg": listChecksIcon,
  "timer-off.svg": timerOffIcon,
  "clock-plus.svg": clockPlusIcon,
  "clock-check.svg": clockCheckIcon,
};

const TimekeepingEmployeeAvatar: React.FC<{ avatar?: string | null }> = ({
  avatar,
}) => {
  const [avatarSrc, setAvatarSrc] = React.useState<string | null>(null);
  const [useFallback, setUseFallback] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    setAvatarSrc(null);
    setUseFallback(false);

    if (!avatar) {
      return;
    }

    loadAvatarFromData(avatar)
      .then((src) => {
        if (!isMounted) return;
        if (src) {
          setAvatarSrc(src);
        } else {
          setUseFallback(true);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setUseFallback(true);
      });

    return () => {
      isMounted = false;
    };
  }, [avatar]);

  const src = !useFallback && avatarSrc ? avatarSrc : undefined;

  return (
    <Avatar
      size={32}
      src={src}
      icon={!src ? <User size={16} /> : undefined}
      onError={() => {
        setUseFallback(true);
        setAvatarSrc(null);
        return false;
      }}
    />
  );
};

export const TimekeepingManagementPage: React.FC = () => {
  const {
    filteredRecords,
    paginatedData,
    loading,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    selectedDate,
    setSelectedDate,
    hasActiveFilter,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    statCards,
    isModalOpen,
    selectedRecord,
    openEditModal,
    closeModal,
    handleApplyFilter,
    handleClearFilter,
    handleSubmitTimekeeping,
    handleDeleteTimekeeping,
    error,
  } = useTimekeepingManagement();

  const columns: ColumnsType<TimekeepingRecord> = [
    {
      title: "Ngày",
      dataIndex: "dateLabel",
      key: "dateLabel",
      width: 140,
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Nhân viên",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 250,
      render: (_, record) => (
        <div className={styles.employeeCell}>
          <TimekeepingEmployeeAvatar avatar={record.avatar} />
          <div className={styles.employeeInfo}>
            <Text strong>{record.employeeName}</Text>
            <Text type="secondary" className={styles.employeeCode}>
              {record.employeeCode}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Ca làm việc",
      dataIndex: "shift",
      key: "shift",
      width: 130,
      align: "right",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Giờ vào",
      dataIndex: "checkIn",
      key: "checkIn",
      width: 100,
      align: "right",
    },
    {
      title: "Giờ ra",
      dataIndex: "checkOut",
      key: "checkOut",
      width: 100,
      align: "right",
    },
    {
      title: "Hệ số",
      dataIndex: "coefficient",
      key: "coefficient",
      width: 80,
      align: "right",
      render: (value: number) => value.toFixed(3),
    },
    {
      title: "Làm thêm",
      dataIndex: "overtimeHours",
      key: "overtimeHours",
      width: 100,
      align: "right",
      render: (value) => `${value}h`,
    },
    {
      title: "Trạng thái",
      dataIndex: "statuses",
      key: "statuses",
      width: 220,
      align: "right",
      render: (statuses: TimekeepingStatus[]) => (
        <Space size={[8, 8]} wrap>
          {statuses.map((status) => (
            <Tag key={status} className={statusTagClass[status]}>
              {status}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Phương thức",
      dataIndex: "methods",
      key: "methods",
      width: 100,
      align: "right",
      render: (_: string[], record: TimekeepingRecord) => {
        const isEdited = record.isEdited || false;
        return (
          <Tooltip title={isEdited ? "Đã chỉnh sửa" : "Định vị"}>
            <div className={styles.methodIcon}>
              <img
                src={isEdited ? userRoundPenIcon : positioningIcon}
                alt={isEdited ? "Đã chỉnh sửa" : "Định vị"}
                className={styles.methodIconImg}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      key: "note",
      width: 130,
      render: (note?: string) => {
        if (!note) {
          return (
            <Text type="secondary" className={styles.noteText}>
              -
            </Text>
          );
        }

        // Loại bỏ STATUS: và OVERTIME: khỏi note để chỉ hiển thị nội dung ghi chú thực tế
        const noteWithoutMetadata = note
          .replace(/STATUS:[^\n]+\n?/g, "")
          .replace(/OVERTIME:[^\n]+\n?/g, "")
          .trim();
        const displayNote = noteWithoutMetadata || "-";

        return (
          <Tooltip
            title={displayNote !== "-" ? displayNote : "Không có ghi chú"}
            placement="topLeft"
          >
            <Text type="secondary" className={styles.noteText}>
              {displayNote}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_: any, record: TimekeepingRecord) => (
        <Space size={0}>
          <Button
            type="text"
            icon={<Pencil size={16} />}
            onClick={() => openEditModal(record)}
            className={styles.actionButton}
          />
          <Popconfirm
            title={null}
            icon={null}
            description="Bạn có chắc chắn muốn xóa bản ghi chấm công này?"
            onConfirm={() => handleDeleteTimekeeping(record)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            placement="left"
            overlayClassName={styles.popconfirmOverlay}
          >
            <Button
              type="text"
              danger
              icon={<Trash2 size={16} />}
              className={styles.actionButton}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc dữ liệu chấm công"
        showDepartmentFilter={false}
        searchPlaceholder="Tìm kiếm theo tên, mã NV, ..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleApplyFilter}
        onClearFilter={handleClearFilter}
        isFilterActive={hasActiveFilter}
        customFilters={
          <>
            <Input
              placeholder="Tìm kiếm theo tên, mã NV, ..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleApplyFilter}
              size="large"
              prefix={
                <img
                  src={searchIcon}
                  alt="search"
                  className={styles.searchIcon}
                />
              }
              className={styles.searchInput}
            />
            <Select
              value={statusFilter}
              onChange={(value) =>
                setStatusFilter(value as TimekeepingStatus | "all")
              }
              size="large"
              className={styles.statusSelect}
            >
              <Option value="all">Tất cả</Option>
              {STATUS_OPTIONS.map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
            <RangePicker
              value={selectedDate}
              onChange={(dates) => {
                setSelectedDate(dates as DateRange);
              }}
              format="DD/MM/YYYY"
              size="large"
              className={styles.datePicker}
              placeholder={["Từ ngày", "Đến ngày"]}
            />
          </>
        }
      />

      <div className={styles.statsSection}>
        {statCards.map((stat) => (
          <div key={stat.title} className={styles.statCard}>
            <div className={styles.statTopRow}>
              <div className={styles.statContent}>
                <Text className={styles.statLabel}>{stat.title}</Text>
                <div className={styles.statValue}>{stat.value}</div>
              </div>
              <div className={styles.statIconBox}>
                <span
                  className={`${styles.statIcon} ${styles.statIconContainer}`}
                >
                  <img
                    src={iconMap[stat.iconSrc]}
                    alt={stat.iconAlt}
                    className={styles.statIconImg}
                  />
                </span>
              </div>
            </div>
            <Text type="secondary" className={styles.statDescription}>
              {stat.description}
            </Text>
          </div>
        ))}
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div>
            <Title level={4} className={styles.tableTitle}>
              Bảng công ngày
            </Title>
            <Text type="secondary">
              Hiển thị {paginatedData.length} / {filteredRecords.length} bản ghi
              chấm công
            </Text>
          </div>
        </div>

        {/* Nếu có lỗi từ hook, hiển thị thông báo lỗi phía trên bảng */}
        {error && <div className={styles.errorMessage}>{error}</div>}

        <Table
          columns={columns}
          dataSource={paginatedData}
          rowKey="id"
          pagination={false}
          className={styles.table}
          loading={loading}
          scroll={{ x: 1000 }}
        />

        <Pagination
          current={currentPage}
          total={filteredRecords.length}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSizeChanger
          showTotal
          itemName="bản ghi"
        />
      </div>

      <TimekeepingModal
        open={isModalOpen}
        onClose={closeModal}
        onSubmit={async (data: TimekeepingFormData) => {
          await handleSubmitTimekeeping(data);
        }}
        initialData={
          selectedRecord
            ? {
                id: selectedRecord.id,
                dateValue: selectedRecord.dateValue,
                employeeName: selectedRecord.employeeName,
                employeeCode: selectedRecord.employeeCode,
                shift: selectedRecord.shift,
                checkIn: selectedRecord.checkIn,
                checkOut: selectedRecord.checkOut,
                coefficient: selectedRecord.coefficient,
                overtimeHours: selectedRecord.overtimeHours,
                statuses: selectedRecord.statuses,
                note: selectedRecord.note,
              }
            : undefined
        }
      />
    </div>
  );
};
